import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components";

import { TestAppState } from "../../app";
import { addBusinessAppNumberCards } from "./cards/business-app-numbers";
import { addCapabilitiesDashboardCards } from "./cards/capabilities";
import { addDistributionChannelOeMatrixCard } from "./cards/distribution-channel-oe-matrix";
import { addDistributionChannelDashboardCards } from "./cards/distribution-channels";
import { addItemAndProjectCountCards } from "./cards/item-and-project-counts";
import { addLifeCycleDashboardCards } from "./cards/life-cycle";
import { addProcessStepsDashboardCards } from "./cards/process-steps";
import { addProgramAndContractsCards } from "./cards/program-and-contracts";
import { addProgramTeamCard } from "./cards/team";
import { addTechnicalExposureDashboardCards } from "./cards/technical-exposure";
import { addTitleCards } from "./cards/title";

type OverviewCard = (
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) => void;

/**
 * Every *static* card the vmd overview page is made of, **in the order they
 * are added**, split out of the ~700-line `buildOverview()` monolith
 * (#2222) — the same way `report-plugin.tsx`'s `buildReportView()` and
 * `test-frontend`'s `overview-plugin.tsx` were split.
 *
 * The order is the point of this array: the drag-and-drop layout places
 * cards by the sequence in which they reach the view, and
 * `overview-plugin.structure.spec.ts` asserts the exact card order, count,
 * and `componentId` values this array produces — including the one card
 * (inside `addBusinessAppNumberCards`) that is only added at all when
 * `isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)` is true. None of that
 * is fixed here; the point of this split is that nothing observable
 * changes.
 *
 * The projects table and the IT-systems table are not in this list: they
 * are added directly by `OverviewPlugin`'s `buildDynamicallyBasedOnAppState`
 * builder, after every entry here, alongside the ~2,600-line table-wiring
 * block (columns, sorting, filtering) that this split deliberately leaves
 * inline — the same precedent as vmd's own `report-plugin.tsx` split
 * (!5350), which left its table-wiring block for a separate follow-up
 * (!5351, still open).
 *
 * Cards that never touch the app declare `(view)` alone; a narrower
 * parameter list is assignable to the wider type, so they need no unused
 * second parameter to sit in this array.
 */
export const OVERVIEW_CARDS: OverviewCard[] = [
  addTitleCards,
  addProgramAndContractsCards,
  addItemAndProjectCountCards,
  addBusinessAppNumberCards,
  addDistributionChannelDashboardCards,
  addDistributionChannelOeMatrixCard,
  addProcessStepsDashboardCards,
  addCapabilitiesDashboardCards,
  addTechnicalExposureDashboardCards,
  addLifeCycleDashboardCards,
  addProgramTeamCard,
];
